import type { CarImageDisplayPreset } from "./car-image-url";

const shownCardImageUrls = new Set<string>();
const paintByLogical = new Map<string, string>();

export function rememberShownCardImage(url: string | null | undefined): void {
  const trimmed = url?.trim();
  if (trimmed) shownCardImageUrls.add(trimmed);
}

export function rememberCardPaintUrl(
  logical: string | null | undefined,
  display: string | null | undefined,
): void {
  const key = logical?.trim();
  const value = display?.trim();
  if (!key || !value) return;
  paintByLogical.set(key, value);
  rememberShownCardImage(key);
  rememberShownCardImage(value);
}

export function cardPaintUrl(logical: string | null | undefined): string | null {
  const key = logical?.trim();
  if (!key) return null;
  return paintByLogical.get(key) ?? null;
}

/** CSS background so the last photo paints before the new <img> decodes. */
export function cardInstantBackground(
  logical: string | null | undefined,
  fallback?: string | null,
): string | undefined {
  const paint = cardPaintUrl(logical) ?? fallback?.trim();
  if (!paint) return undefined;
  const escaped = paint.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

export function wasCardImageShown(url: string | null | undefined): boolean {
  const trimmed = url?.trim();
  return Boolean(trimmed && shownCardImageUrls.has(trimmed));
}

export function clearShownCardImages(): void {
  shownCardImageUrls.clear();
  paintByLogical.clear();
}

export function shouldEagerLoadCarImage(opts: {
  src?: string | null;
  preset: CarImageDisplayPreset;
  priority?: boolean;
}): boolean {
  if (opts.priority) return true;
  if (opts.preset === "card") return true;
  return wasCardImageShown(opts.src);
}

export type RevivableCardPhoto = {
  loading: string;
  src: string;
  currentSrc?: string;
  complete?: boolean;
  naturalWidth?: number;
};

export type PhotoRoot = {
  querySelectorAll: (selector: string) => ArrayLike<RevivableCardPhoto>;
};

/** Kick decode on remount / bfcache so listing tiles are not left as a black box. */
export function reviveCardPhotosIn(root: PhotoRoot): number {
  const photos = root.querySelectorAll("img.car-card-photo");
  let revived = 0;
  for (let i = 0; i < photos.length; i++) {
    const img = photos[i];
    if (!img) continue;
    const src = (img.currentSrc || img.src || "").trim();
    if (!src) continue;
    img.loading = "eager";
    const alreadyPainted = Boolean(img.complete && (img.naturalWidth ?? 0) > 0);
    if (!alreadyPainted) {
      img.src = src;
    }
    rememberShownCardImage(src);
    revived += 1;
  }
  return revived;
}

function onPageShow(event: PageTransitionEvent) {
  if (typeof document === "undefined") return;
  if (event.persisted) {
    reviveCardPhotosIn(document);
  }
}

export function bindCardPhotoPageShow(target: {
  addEventListener: (type: "pageshow", listener: (event: PageTransitionEvent) => void) => void;
} = typeof window !== "undefined" ? window : { addEventListener: () => undefined }): void {
  target.addEventListener("pageshow", onPageShow);
}

if (typeof window !== "undefined") {
  bindCardPhotoPageShow(window);
}
